/**
 * Main hook for patient context data
 *
 * Orchestrates fetching patient context data (medications, diagnoses,
 * allergies, last encounter, treatment plan). Clinical lists come from
 * the v2 /info endpoint so the bar stays in sync with the patient page.
 */

import { useMemo } from 'react';
import { usePatientInfoQuery } from '@features/patients';

import {
  useGetPatientPreRecordingInfoQuery,
  useGetTreatmentPlanQuery,
} from '../store/api';
import type {
  Allergy,
  Diagnosis,
  LastEncounterData,
  Medication,
  PatientContextData,
  PatientContextLoadingState,
  TreatmentPlanData,
} from '../types';

export interface UsePatientContextReturn extends PatientContextData {
  isLoading: boolean;
  loadingState: PatientContextLoadingState;
}

/**
 * Transform pre-recording info response to LastEncounterData
 */
function transformLastEncounter(data: any): LastEncounterData | null {
  if (!data) return null;

  return {
    past_visit_summary: data.past_visit_summary,
    latest_encounter: data.patient?.latest_encounter,
  };
}

const EMPTY_ALLERGIES: Allergy[] = [];
const EMPTY_DIAGNOSES: Diagnosis[] = [];
const EMPTY_MEDICATIONS: Medication[] = [];

/**
 * Hook for fetching and transforming patient context data
 *
 * @param patientId - Patient ID to fetch context for (null skips fetching)
 * @returns Patient context data with loading states
 */
export function usePatientContext(patientId: string | null): UsePatientContextReturn {
  // Fetch patient info (v2 — shared with the patient page)
  const {
    data: patientInfo,
    isLoading: isLoadingPatientInfo,
  } = usePatientInfoQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  // Fetch pre-recording info (last encounter) — still v1
  const {
    data: preRecordingInfo,
    isLoading: isLoadingPreRecordingInfo,
    error: preRecordingError,
  } = useGetPatientPreRecordingInfoQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  // Fetch treatment plan — v2
  const {
    data: treatmentPlanData,
    isLoading: isLoadingTreatmentPlan,
    error: treatmentPlanError,
  } = useGetTreatmentPlanQuery(
    { patientId: patientId! },
    { skip: !patientId },
  );

  const medications = patientInfo?.medications ?? EMPTY_MEDICATIONS;
  const diagnoses = patientInfo?.diagnoses ?? EMPTY_DIAGNOSES;
  const allergies = patientInfo?.allergies ?? EMPTY_ALLERGIES;

  const lastEncounter = useMemo(() => {
    if (!preRecordingInfo || preRecordingError) return null;
    return transformLastEncounter(preRecordingInfo);
  }, [preRecordingInfo, preRecordingError]);

  const treatmentPlan = useMemo((): TreatmentPlanData | null => {
    if (!treatmentPlanData || treatmentPlanError) return null;
    return {
      markdown: treatmentPlanData.markdown_content || '',
      treatment_plan_id: treatmentPlanData.treatment_plan_id || null,
      must_be_updated_by: treatmentPlanData.must_be_updated_by ?? null,
      updated_at: treatmentPlanData.updated_at ?? null,
      updated_by: treatmentPlanData.updated_by ?? null,
    };
  }, [treatmentPlanData, treatmentPlanError]);

  const loadingState: PatientContextLoadingState = useMemo(
    () => ({
      patient: false, // Patient is passed in, not fetched here
      medications: isLoadingPatientInfo,
      diagnoses: isLoadingPatientInfo,
      allergies: isLoadingPatientInfo,
      lastEncounter: isLoadingPreRecordingInfo,
      treatmentPlan: isLoadingTreatmentPlan,
    }),
    [isLoadingPatientInfo, isLoadingPreRecordingInfo, isLoadingTreatmentPlan],
  );

  const isLoading = isLoadingPatientInfo || isLoadingPreRecordingInfo || isLoadingTreatmentPlan;

  return {
    patient: null, // Patient should be passed separately
    medications,
    diagnoses,
    allergies,
    lastEncounter,
    treatmentPlan,
    isLoading,
    loadingState,
  };
}
